import type { Entity } from '@/types/Entity';
import { read, utils } from '@e965/xlsx';

interface CatalogEntityData {
  nsn?: string;
  parent_id?: string;
  size?: string;
}

export interface ParsedSheetRow {
  rowNumber: number;
  cells: string[];
}

export interface PersonSizingImportSheet {
  headers: string[];
  rows: ParsedSheetRow[];
}

interface DetectedColumns {
  childNsn: number;
  parentNsn: number;
  sizeLabel: number;
  childName: number;
}

interface CatalogLookups {
  parentById: Map<string, Entity>;
  parentByNsn: Map<string, Entity[]>;
  childByNsn: Map<string, Entity[]>;
  childrenByParent: Map<string, Entity[]>;
}

export interface ImportedSizingDraft {
  catalogId: string;
  parentId: string;
  /** The NSN value used to resolve this row (for error reporting). */
  sourceNsn: string;
  /** Display name of the resolved parent item (for error reporting). */
  parentDisplayName: string;
}

export interface PersonSizingImportResult {
  importedDrafts: Record<string, ImportedSizingDraft>;
  importedCount: number;
}

export interface BuildPersonSizingImportOptions {
  sheet: PersonSizingImportSheet;
  catalogItems: Entity[];
}

const CHILD_NSN_ALIASES = [
  'size nsn',
  'sized nsn',
  'child nsn',
  'variant nsn',
  'catalog nsn',
  'nsn',
];
const PARENT_NSN_ALIASES = ['parent nsn', 'parent item nsn', 'unsized nsn', 'item nsn'];
const SIZE_LABEL_ALIASES = ['size', 'size label', 'size_name', 'size name'];
const CHILD_NAME_ALIASES = [
  'size nomenclature',
  'child name',
  'sized nomenclature',
  'nomenclature',
];
const MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;
const MAX_IMPORT_FILE_SIZE_LABEL = '10 MB';
const MAX_IMPORT_ROWS = 2000;

const normalizeHeader = (value: string): string =>
  value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

const normalizeValue = (value: string): string => value.trim().toLowerCase();

const toCellString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  return '';
};

const isBlankRow = (cells: string[]): boolean => cells.every((cell) => cell.trim().length === 0);

const findColumnIndex = (headers: string[], aliases: string[]): number => {
  const normalizedHeaders = headers.map(normalizeHeader);
  const normalizedAliases = aliases.map(normalizeHeader);
  return normalizedHeaders.findIndex((header) => normalizedAliases.includes(header));
};

const getColumnLabel = (headers: string[], index: number): string => {
  if (index < 0) return 'Unknown';
  return headers[index] || `Column ${index + 1}`;
};

const buildRowColumnError = (rowNumber: number, columnLabel: string, message: string): Error => {
  return new Error(`Row ${rowNumber}, column "${columnLabel}": ${message}`);
};

const addLookupEntry = (map: Map<string, Entity[]>, key: string, value: Entity): void => {
  const current = map.get(key);
  if (current) {
    current.push(value);
    return;
  }
  map.set(key, [value]);
};

const getCatalogLookups = (catalogItems: Entity[]): CatalogLookups => {
  const parentById = new Map<string, Entity>();
  const parentByNsn = new Map<string, Entity[]>();
  const childByNsn = new Map<string, Entity[]>();
  const childrenByParent = new Map<string, Entity[]>();

  catalogItems.forEach((item) => {
    const data = (item.entity_data ?? {}) as CatalogEntityData;
    const parentId = typeof data.parent_id === 'string' ? data.parent_id.trim() : '';
    const rawNsn = typeof data.nsn === 'string' ? normalizeValue(data.nsn) : '';

    if (parentId.length > 0) {
      const currentChildren = childrenByParent.get(parentId) ?? [];
      currentChildren.push(item);
      childrenByParent.set(parentId, currentChildren);

      if (rawNsn) {
        addLookupEntry(childByNsn, rawNsn, item);
      }
      return;
    }

    parentById.set(item.id, item);
    if (rawNsn) {
      addLookupEntry(parentByNsn, rawNsn, item);
    }
  });

  return { parentById, parentByNsn, childByNsn, childrenByParent };
};

const resolveUniqueParentByNsn = (
  lookups: CatalogLookups,
  parentNsn: string,
  rowNumber: number,
  columnLabel: string,
): Entity => {
  const matches = lookups.parentByNsn.get(normalizeValue(parentNsn)) ?? [];
  if (matches.length === 0) {
    throw buildRowColumnError(
      rowNumber,
      columnLabel,
      `No parent catalog item found for NSN "${parentNsn}".`,
    );
  }
  if (matches.length > 1) {
    throw buildRowColumnError(
      rowNumber,
      columnLabel,
      `Multiple parent catalog items found for NSN "${parentNsn}".`,
    );
  }
  return matches[0]!;
};

const resolveUniqueChildByNsn = (
  lookups: CatalogLookups,
  childNsn: string,
  rowNumber: number,
  columnLabel: string,
): Entity | null => {
  const matches = lookups.childByNsn.get(normalizeValue(childNsn)) ?? [];
  if (matches.length === 0) return null;
  if (matches.length > 1) {
    throw buildRowColumnError(
      rowNumber,
      columnLabel,
      `Multiple sized catalog items found for NSN "${childNsn}".`,
    );
  }
  return matches[0]!;
};

const scoreChildMatch = (child: Entity, needle: string): number => {
  const data = (child.entity_data ?? {}) as CatalogEntityData;
  const sizeLabel = typeof data.size === 'string' ? normalizeValue(data.size) : '';
  const displayName = normalizeValue(child.display_name ?? '');
  const entityIdentifier = normalizeValue(child.entity_identifier ?? '');
  const normalizedNeedle = normalizeValue(needle);

  if (!normalizedNeedle) return 0;
  if (sizeLabel && sizeLabel === normalizedNeedle) return 3;
  if (displayName === normalizedNeedle || entityIdentifier === normalizedNeedle) return 2;
  if (
    (sizeLabel && sizeLabel.includes(normalizedNeedle)) ||
    displayName.includes(normalizedNeedle) ||
    entityIdentifier.includes(normalizedNeedle)
  ) {
    return 1;
  }
  return 0;
};

const resolveChildFromParentAndSize = (
  lookups: CatalogLookups,
  parent: Entity,
  sizeValue: string,
  rowNumber: number,
  columnLabel: string,
): Entity => {
  const children = lookups.childrenByParent.get(parent.id) ?? [];
  if (children.length === 0) {
    throw buildRowColumnError(
      rowNumber,
      columnLabel,
      `Parent "${parent.display_name}" has no sized variants.`,
    );
  }

  const scored = children
    .map((child) => ({ child, score: scoreChildMatch(child, sizeValue) }))
    .filter((entry) => entry.score > 0);

  if (scored.length === 0) {
    throw buildRowColumnError(
      rowNumber,
      columnLabel,
      `No size under parent "${parent.display_name}" matches "${sizeValue}".`,
    );
  }

  const highestScore = Math.max(...scored.map((entry) => entry.score));
  const highestMatches = scored
    .filter((entry) => entry.score === highestScore)
    .map((entry) => entry.child);

  if (highestMatches.length > 1) {
    throw buildRowColumnError(
      rowNumber,
      columnLabel,
      `Multiple sizes under parent "${parent.display_name}" match "${sizeValue}".`,
    );
  }

  return highestMatches[0]!;
};

const detectColumns = (headers: string[]): DetectedColumns => {
  const detected: DetectedColumns = {
    childNsn: findColumnIndex(headers, CHILD_NSN_ALIASES),
    parentNsn: findColumnIndex(headers, PARENT_NSN_ALIASES),
    sizeLabel: findColumnIndex(headers, SIZE_LABEL_ALIASES),
    childName: findColumnIndex(headers, CHILD_NAME_ALIASES),
  };

  const hasChildNsn = detected.childNsn >= 0;
  const hasParentAndSizeHint =
    detected.parentNsn >= 0 && (detected.sizeLabel >= 0 || detected.childName >= 0);

  if (!hasChildNsn && !hasParentAndSizeHint) {
    throw new Error(
      'Unable to detect sizing columns. Provide either a size NSN column (for example "NSN" or "Size NSN"), or provide "Parent NSN" with "Size".',
    );
  }

  return detected;
};

const getCell = (row: ParsedSheetRow, columnIndex: number): string => {
  if (columnIndex < 0) return '';
  return row.cells[columnIndex] ?? '';
};

const resolveRowDraft = (
  row: ParsedSheetRow,
  headers: string[],
  columns: DetectedColumns,
  lookups: CatalogLookups,
): ImportedSizingDraft => {
  const childNsnValue = getCell(row, columns.childNsn);
  const parentNsnValue = getCell(row, columns.parentNsn);
  const sourceNsn = childNsnValue || parentNsnValue;
  const sizeLabelValue = getCell(row, columns.sizeLabel);
  const childNameValue = getCell(row, columns.childName);
  const sizeHintValue = sizeLabelValue || childNameValue;

  let child: Entity | null = null;

  if (childNsnValue) {
    child = resolveUniqueChildByNsn(
      lookups,
      childNsnValue,
      row.rowNumber,
      getColumnLabel(headers, columns.childNsn),
    );

    if (!child && sizeHintValue) {
      const parentLookupValue = parentNsnValue || childNsnValue;
      const parentLookupColumn = parentNsnValue
        ? getColumnLabel(headers, columns.parentNsn)
        : getColumnLabel(headers, columns.childNsn);
      const inferredParent = resolveUniqueParentByNsn(
        lookups,
        parentLookupValue,
        row.rowNumber,
        parentLookupColumn,
      );
      child = resolveChildFromParentAndSize(
        lookups,
        inferredParent,
        sizeHintValue,
        row.rowNumber,
        sizeLabelValue
          ? getColumnLabel(headers, columns.sizeLabel)
          : getColumnLabel(headers, columns.childName),
      );
    }

    if (!child) {
      throw buildRowColumnError(
        row.rowNumber,
        getColumnLabel(headers, columns.childNsn),
        `No sized catalog item found for NSN "${childNsnValue}".`,
      );
    }
  }

  let resolvedParent: Entity | null = null;

  if (child) {
    const childData = (child.entity_data ?? {}) as CatalogEntityData;
    const parentId = typeof childData.parent_id === 'string' ? childData.parent_id.trim() : '';
    if (!parentId) {
      throw buildRowColumnError(
        row.rowNumber,
        getColumnLabel(headers, columns.childNsn),
        `Catalog item "${child.display_name}" is not a sized variant.`,
      );
    }

    resolvedParent = lookups.parentById.get(parentId) ?? null;
    if (!resolvedParent) {
      throw buildRowColumnError(
        row.rowNumber,
        getColumnLabel(headers, columns.childNsn),
        `Unable to find parent catalog item for "${child.display_name}".`,
      );
    }
  }

  if (!resolvedParent) {
    if (!parentNsnValue) {
      const column = columns.childNsn >= 0 ? getColumnLabel(headers, columns.childNsn) : 'NSN';
      throw buildRowColumnError(
        row.rowNumber,
        column,
        'Missing NSN. Provide a sized NSN or provide Parent NSN with a Size value.',
      );
    }

    resolvedParent = resolveUniqueParentByNsn(
      lookups,
      parentNsnValue,
      row.rowNumber,
      getColumnLabel(headers, columns.parentNsn),
    );

    if (!sizeHintValue) {
      throw buildRowColumnError(
        row.rowNumber,
        getColumnLabel(headers, columns.parentNsn),
        'Missing size details. Provide a sized NSN or a Size value.',
      );
    }

    child = resolveChildFromParentAndSize(
      lookups,
      resolvedParent,
      sizeHintValue,
      row.rowNumber,
      sizeLabelValue
        ? getColumnLabel(headers, columns.sizeLabel)
        : getColumnLabel(headers, columns.childName),
    );
  }

  if (parentNsnValue) {
    const expectedParent = resolveUniqueParentByNsn(
      lookups,
      parentNsnValue,
      row.rowNumber,
      getColumnLabel(headers, columns.parentNsn),
    );
    if (expectedParent.id !== resolvedParent.id) {
      throw buildRowColumnError(
        row.rowNumber,
        getColumnLabel(headers, columns.parentNsn),
        `Parent NSN "${parentNsnValue}" does not match sized item "${child?.display_name}".`,
      );
    }
  }

  if (!child) {
    throw buildRowColumnError(
      row.rowNumber,
      columns.childNsn >= 0 ? getColumnLabel(headers, columns.childNsn) : 'NSN',
      'Unable to resolve sized catalog item for this row.',
    );
  }

  return {
    catalogId: child.id,
    parentId: resolvedParent.id,
    sourceNsn,
    parentDisplayName: resolvedParent.display_name ?? resolvedParent.id,
  };
};

export const parsePersonSizingImportSheet = async (
  file: File,
): Promise<PersonSizingImportSheet> => {
  if (file.size > MAX_IMPORT_FILE_BYTES) {
    throw new Error(
      `"${file.name}" is too large. Maximum file size is ${MAX_IMPORT_FILE_SIZE_LABEL}.`,
    );
  }

  const bytes = await file.arrayBuffer();
  let workbook;

  try {
    workbook = read(bytes, { type: 'array', raw: false });
  } catch {
    throw new Error(`Unable to read "${file.name}". Upload a valid .xlsx, .xls, or .csv file.`);
  }

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error(`"${file.name}" does not contain any sheets.`);
  }

  const sheet = workbook.Sheets[firstSheetName];
  if (!sheet) {
    throw new Error(`Unable to read the first sheet in "${file.name}".`);
  }

  const matrix = utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: false,
    defval: '',
    blankrows: false,
  });

  if (matrix.length === 0) {
    throw new Error(`"${file.name}" is empty.`);
  }

  const headers = (matrix[0] ?? []).map(toCellString);
  if (headers.every((header) => header.length === 0)) {
    throw new Error(`"${file.name}" is missing a header row.`);
  }

  const rows: ParsedSheetRow[] = matrix
    .slice(1)
    .map((row, index) => ({ rowNumber: index + 2, cells: row.map(toCellString) }))
    .filter((row) => !isBlankRow(row.cells));

  if (rows.length === 0) {
    throw new Error(`"${file.name}" has no data rows to import.`);
  }
  if (rows.length > MAX_IMPORT_ROWS) {
    throw new Error(
      `"${file.name}" has ${rows.length} data rows. Maximum allowed is ${MAX_IMPORT_ROWS} rows.`,
    );
  }

  return { headers, rows };
};

export const buildPersonSizingImportFromSheet = ({
  sheet,
  catalogItems,
}: BuildPersonSizingImportOptions): PersonSizingImportResult => {
  const lookups = getCatalogLookups(catalogItems);
  const columns = detectColumns(sheet.headers);
  const importedDrafts: Record<string, ImportedSizingDraft> = {};
  const parentToDraft = new Map<string, { catalogId: string; rowNumber: number; sourceNsn: string }>();

  sheet.rows.forEach((row) => {
    const draft = resolveRowDraft(row, sheet.headers, columns, lookups);
    const prior = parentToDraft.get(draft.parentId);

    if (prior) {
      const currentNsn = draft.sourceNsn;
      const priorNsn = prior.sourceNsn;
      const parentName = draft.parentDisplayName;
      throw buildRowColumnError(
        row.rowNumber,
        getColumnLabel(sheet.headers, columns.childNsn >= 0 ? columns.childNsn : columns.parentNsn),
        `Duplicate sizing for item "${parentName}" — this row (NSN "${currentNsn}") and row ${prior.rowNumber} (NSN "${priorNsn}") both resolve to sizes of the same parent item. Only one size per item is allowed.`,
      );
    }

    parentToDraft.set(draft.parentId, { catalogId: draft.catalogId, rowNumber: row.rowNumber, sourceNsn: draft.sourceNsn });
    importedDrafts[draft.catalogId] = draft;
  });

  const importedCount = Object.keys(importedDrafts).length;
  if (importedCount === 0) {
    throw new Error('No sizing assignments were detected in the import file.');
  }

  return { importedDrafts, importedCount };
};

export const parsePersonSizingImportFile = async (
  file: File,
  catalogItems: Entity[],
): Promise<PersonSizingImportResult> => {
  const sheet = await parsePersonSizingImportSheet(file);
  return buildPersonSizingImportFromSheet({ sheet, catalogItems });
};
